import Input from "components/input-fields/input-fields";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import FlagComponent from "components/flag/flag.component";
import { validatePhone } from "helpers/data";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Spinner } from "react-bootstrap";
import { useAuthContext } from "store/auth";
import { client } from "helpers/client";
import { usePreregisterData } from "../registration/registration.service";
import { passwordPattern } from "../../helpers/patterns";

const useValidate = () => {
  const { t } = useTranslation();
  const schema = yup.object().shape({
    name: yup.string().required(t("errors:required")),
    email: yup.string().email().required(t("errors:required")),
    companyName: yup.string().required(t("errors:required")),
    password: yup.string().required(t("errors:required")).matches(passwordPattern, t("errors:password")),
    phone: yup
      .string()
      .required(t("errors:required"))
      .test("validatePhone", t("errors:invalidFormat"), (value, { parent }) => !!validatePhone(value, parent.countryCode)),
    countryCode: yup.string().required()
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useMemo(() => schema, [t]);
};

const TempRegister = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const schema = useValidate();
  const [, userDispatch] = useAuthContext();
  const { preregisterData, setPreregisterData } = usePreregisterData();

  const {
    register,
    control,
    formState: { errors },
    handleSubmit
  } = useForm({
    defaultValues: {
      name: "",
      email: "",
      password: "",
      phone: "",
      countryCode: "20"
    },
    resolver: yupResolver(schema)
  });

  const { mutate, isLoading } = useMutation((data) => client("/auth/registerLandingPage", { data }));

  const registerUser = (data) => {
    const phone = validatePhone(data.phone, data.countryCode).getNumber();

    const dto = {
      ...data,
      mobile: phone,
      sourceId: 1
    };

    mutate(dto, {
      onSuccess: ({ response }) => {
        if (response) {
          console.log(response);
          setPreregisterData(response);
        }
      }
    });
  };

  useEffect(() => {
    if (preregisterData) navigate("/temp-validate");
  }, [navigate, preregisterData, setPreregisterData]);

  return (
    <div className="registration">
      <div className="row">
        <div className="col-lg-8 mx-auto">
          <div className="row">
            <div className="col-lg-6">
              <h1 className="title">
                <p>{t("signup:title")}</p>
                <span className="text-center">{t("signup:signup")}</span>
              </h1>
            </div>
            <form onSubmit={handleSubmit(registerUser)} className="col-lg-6">
              <Input id="name" label={t("signup:name")} {...register("name")} error={errors.name?.message} />
              <Input id="email" label={t("signup:email")} {...register("email")} error={errors.email?.message} />
              <Input
                id="companyName"
                label={t("signup:company")}
                {...register("companyName")}
                error={errors.companyName?.message}
              />
              <div className="row">
                <div className="col-md-2">
                  <Controller
                    rules={{ required: "required" }}
                    render={({ field }) => (
                      <FlagComponent error={errors.countryCode?.message} onChange={field.onChange} value={field.value} />
                    )}
                    name="countryCode"
                    control={control}
                  />
                </div>
                <div className="col-md-10">
                  <Input {...register("phone")} error={errors.phone?.message} id="phoneNumber" label={t("signup:phone")} />
                </div>
              </div>
              <Input
                id="password"
                label={t("signup:password")}
                type="password"
                {...register("password")}
                error={errors.password?.message}
              />

              <button type="submit" className={`btn-blue`} disabled={isLoading}>
                {isLoading && <Spinner animation="border" size="sm" />}
                {t("signup:submit")}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TempRegister;
